/**
 * Making two thousand notifications findable.
 *
 * The bell drops down a flat reverse-chronological list with no way to
 * narrow it. There are ~2,000 rows across sixteen kinds, and the ones
 * somebody is actually waiting on — a mention, a message, an approval —
 * sit between hundreds of stage changes nobody needs to read.
 *
 * Priority is DERIVED from the kind, not stored. There is no priority
 * column and inventing one would mean every writer across the app
 * remembering to set it; the kind already says what happened, so the
 * judgement about how much it matters lives here, in one list somebody
 * can read and argue with.
 */

/** Someone is waiting on you personally. */
const NEEDS_YOU = new Set([
  'mention',
  'deal_message',
  'invoice_approval_required',
  'todo_assigned',
  'pricing_request',
]);

/** Nobody is waiting, but something is slipping. */
const AT_RISK = new Set([
  'deal_sla_breach',
  'esign_expiring',
  'esign_unsigned',
  'esign_unopened',
  // The 45-day auto-pass is gone and nothing emits this any more, but 15
  // of them are already in the table. Classify them, or they fall
  // through to no bucket and vanish from the bell they were sent to.
  'deal_auto_passed',
  'deal_followup',
]);

/** It happened; read it if you like. */
const FYI = new Set([
  'deal_stage',
  'pricing_sync',
  'prelim-title',
  'buyer-area',
  'esign_signed',
]);

export const PRIORITIES = Object.freeze([
  { id: 'needs-you', label: 'Needs you' },
  { id: 'at-risk', label: 'At risk' },
  { id: 'fyi', label: 'FYI' },
]);

/**
 * Which bucket a notification falls in.
 *
 * An unrecognised kind is treated as NEEDS YOU, deliberately. A kind
 * added later and never classified here would otherwise land in FYI and
 * be filtered out of sight by somebody narrowing to what matters — a
 * filter that quietly hides work is worse than one that shows too much,
 * because the noise is obvious and the omission is not.
 */
export function notificationPriority(notification) {
  const kind = String(notification?.kind || '').trim();
  if (AT_RISK.has(kind)) return 'at-risk';
  if (FYI.has(kind)) return 'fyi';
  if (NEEDS_YOU.has(kind)) return 'needs-you';
  return 'needs-you';
}

/** True when this kind is classified rather than falling through. */
export function isKnownNotificationKind(kind) {
  const value = String(kind || '').trim();
  return NEEDS_YOU.has(value) || AT_RISK.has(value) || FYI.has(value);
}

export const DATE_RANGES = Object.freeze([
  { id: 'all', label: 'Any time', days: null },
  { id: 'today', label: 'Today', days: 1 },
  { id: 'week', label: 'Last 7 days', days: 7 },
  { id: 'month', label: 'Last 30 days', days: 30 },
]);

/**
 * Is this notification inside the window?
 *
 * "Today" means the last 24 hours rather than since midnight: the bell is
 * read at 9am about things that happened at 6pm yesterday, and a window
 * that resets at midnight hides exactly those.
 */
export function withinRange(notification, rangeId, now = Date.now()) {
  const range = DATE_RANGES.find((entry) => entry.id === rangeId);
  if (!range || range.days === null) return true;
  const at = Date.parse(notification?.createdAt || '');
  // Undated rows are kept rather than filtered away. A missing timestamp
  // is a data problem, and hiding the row makes it an invisible one.
  if (!Number.isFinite(at)) return true;
  return now - at <= range.days * 24 * 60 * 60 * 1000;
}

/** The deal a notification is about, if any. */
export function notificationDealId(notification) {
  const id = notification?.meta?.dealId;
  const text = typeof id === 'string' ? id.trim() : '';
  return text || null;
}

/**
 * The deals represented in a list, for the picker.
 *
 * Labelled from the deals roster where it can be, and from the id where
 * it cannot — a notification about a deal that has since been archived
 * still has to be selectable, or its rows become unreachable.
 */
export function notificationDealOptions(notifications, deals) {
  const byId = new Map();
  for (const deal of deals || []) {
    const id = typeof deal?.id === 'string' ? deal.id : '';
    if (id) byId.set(id, deal);
  }
  const counts = new Map();
  for (const notification of notifications || []) {
    const id = notificationDealId(notification);
    if (!id) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  const options = [];
  for (const [id, count] of counts) {
    const deal = byId.get(id);
    const name = (typeof deal?.ownerName === 'string' && deal.ownerName.trim()) || id;
    options.push({ id, name, count });
  }
  options.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return options;
}

/**
 * Apply the lot. An unset filter is not a filter.
 */
export function filterNotifications(notifications, {
  priority = 'all', range = 'all', dealId = 'all', unreadOnly = false, now = Date.now(),
} = {}) {
  return (notifications || []).filter((notification) => {
    if (!notification) return false;
    if (unreadOnly && notification.readAt) return false;
    if (priority !== 'all' && notificationPriority(notification) !== priority) return false;
    if (range !== 'all' && !withinRange(notification, range, now)) return false;
    if (dealId !== 'all' && notificationDealId(notification) !== dealId) return false;
    return true;
  });
}

/**
 * Where a notification should actually land.
 *
 * A message or a mention is about something somebody SAID, so it opens
 * the deal's discussion rather than dropping you on the deal to go
 * looking for it. The deal page already takes ?openDiscussion=1; adding
 * it here rather than at the writer means the hundreds of message
 * notifications already in the table start working too.
 */
const DISCUSSION_KINDS = new Set(['deal_message', 'mention']);

export function notificationTarget(notification) {
  const path = typeof notification?.linkPath === 'string' ? notification.linkPath.trim() : '';
  if (!path) return null;
  if (!DISCUSSION_KINDS.has(String(notification?.kind || ''))) return path;
  // Never a second one — these paths already carry ?deal=…
  if (/[?&]openDiscussion=/.test(path)) return path;
  return `${path}${path.includes('?') ? '&' : '?'}openDiscussion=1`;
}
